/**
 * Jogo da velha.
 *
 * Notacao de lance: indice da casa, "0".."8", contando da esquerda para a
 * direita e de cima para baixo.
 */
import { type GameRules, IllegalMoveError, type Move, type Outcome, type Seat } from './types.ts';

/** 9 casas: null = vazia. */
export type TicTacToeState = {
  readonly board: readonly (Seat | null)[];
  readonly turn: Seat;
};

const LINES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function winner(board: readonly (Seat | null)[]): Seat | null {
  for (const [a, b, c] of LINES) {
    const v = board[a];
    if (v !== null && v !== undefined && v === board[b] && v === board[c]) return v;
  }
  return null;
}

export const ticTacToe: GameRules<TicTacToeState> = {
  id: 'TIC_TAC_TOE',

  initial: () => ({ board: Array<Seat | null>(9).fill(null), turn: 0 }),

  turn: (state) => state.turn,

  legalMoves(state) {
    if (winner(state.board) !== null) return [];
    return state.board.flatMap((cell, i) => (cell === null ? [String(i)] : []));
  },

  apply(state, move) {
    const index = Number(move);
    if (!Number.isInteger(index) || index < 0 || index > 8) {
      throw new IllegalMoveError(move, 'casa fora do tabuleiro');
    }
    if (winner(state.board) !== null) {
      throw new IllegalMoveError(move, 'partida ja terminada');
    }
    if (state.board[index] !== null) {
      throw new IllegalMoveError(move, 'casa ocupada');
    }
    const board = [...state.board];
    board[index] = state.turn;
    return { board, turn: state.turn === 0 ? 1 : 0 };
  },

  outcome(state): Outcome | null {
    const w = winner(state.board);
    if (w !== null) return { kind: 'WIN', seat: w, reason: 'TRES_EM_LINHA' };
    if (state.board.every((c) => c !== null)) return { kind: 'DRAW', reason: 'TABULEIRO_CHEIO' };
    return null;
  },

  serialize: (state) =>
    `${state.board.map((c) => (c === null ? '-' : String(c))).join('')}|${state.turn}`,

  deserialize(text) {
    const [cells, turn] = text.split('|');
    if (cells?.length !== 9 || (turn !== '0' && turn !== '1')) {
      throw new Error(`estado invalido: ${text}`);
    }
    return {
      board: [...cells].map((c) => (c === '-' ? null : (Number(c) as Seat))),
      turn: Number(turn) as Seat,
    };
  },
};

export type { Move };
